// src/features/analytics/DashboardFormDialog.tsx
// Create / edit a dashboard's basic properties. CreateDashboard, UpdateDashboard.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Textarea, Button, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { analyticsApi } from '@core/api';
import type { Dashboard, DashboardVisibility } from '@core/types/analytics';
import { AnalyticsModal } from './Modal';
import { DASHBOARD_VISIBILITIES, errMessage, makeEntity, parseClearableInt, type ScopeIds } from './helpers';

interface DashboardFormDialogProps {
  open: boolean;
  onClose: () => void;
  scope: ScopeIds;
  dashboard?: Dashboard | null;
  onSaved: (dashboard: Dashboard) => void;
}

export function DashboardFormDialog({ open, onClose, scope, dashboard, onSaved }: DashboardFormDialogProps) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [visibility, setVisibility] = useState<DashboardVisibility>('DASHBOARD_VISIBILITY_PRIVATE');
  const [autoRefresh, setAutoRefresh] = useState<number | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(dashboard?.name ?? '');
    setDescription(dashboard?.description ?? '');
    setSlug(dashboard?.slug ?? '');
    setVisibility(dashboard?.visibility ?? 'DASHBOARD_VISIBILITY_PRIVATE');
    setAutoRefresh(dashboard?.auto_refresh_seconds || undefined);
  }, [open, dashboard]);

  const save = async () => {
    if (!name.trim()) {
      setError(t('analytics.dashboards.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (dashboard?.entity?.id) {
        const res = await analyticsApi.UpdateDashboard(dashboard.entity.id, {
          dashboard: {
            ...dashboard,
            name: name.trim(),
            description,
            slug,
            visibility,
            auto_refresh_seconds: autoRefresh ?? 0,
          },
        });
        if (res.dashboard) onSaved(res.dashboard);
      } else {
        const res = await analyticsApi.CreateDashboard({
          entity: makeEntity('', 'dashboard', scope),
          name: name.trim(),
          description,
          slug: slug || undefined,
          visibility,
          auto_refresh_seconds: autoRefresh,
        });
        if (res.dashboard) onSaved(res.dashboard);
      }
      onClose();
    } catch (e) {
      setError(errMessage(e, t('analytics.dashboards.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnalyticsModal
      open={open}
      onClose={onClose}
      title={dashboard ? t('analytics.dashboards.edit') : t('analytics.dashboards.create')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      <div className="space-y-3">
        <Input label={t('analytics.dashboards.name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Textarea label={t('analytics.dashboards.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        <Input label={t('analytics.dashboards.slug')} value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="sales-overview" />
        <div className="grid grid-cols-2 gap-3">
          <Select label={t('analytics.dashboards.visibility')} value={visibility} onChange={(e) => setVisibility(e.target.value as DashboardVisibility)}>
            {DASHBOARD_VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {t(`analytics.visibility.${v}`)}
              </option>
            ))}
          </Select>
          <Input
            label={t('analytics.dashboards.autoRefresh')}
            value={autoRefresh !== undefined ? String(autoRefresh) : ''}
            onChange={(e) => setAutoRefresh(parseClearableInt(e.target.value))}
            inputMode="numeric"
            helperText={t('analytics.dashboards.autoRefreshHelp')}
          />
        </div>
      </div>
    </AnalyticsModal>
  );
}
